import type { CanvasCompileStatus } from "@ando/core";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type * as React from "react";

/**
 * CanvasStatusChip — visual badge for the compile lifecycle.
 *
 * v1 ref: ``ando-frontend/app/lib/canvasStatusTokens.ts`` + the per-status
 * pill in ``CanvasCard.tsx``. v2 inlines the token map here to keep the
 * feature self-contained (no shared lib reach-through needed for a 5-status
 * vocabulary).
 *
 * Colour palette mirrors the v2 ``technical.tsx`` STATUS_CHIP pattern and
 * stays inside the warm "pencil" range: amber for in-flight, green for
 * ready, red for failed, a warm ando-orange hairline contour for the
 * actionable "aggiornabile" state, and a warm stone/neutral for archived.
 */

interface StatusMeta {
  readonly label: MessageDescriptor;
  readonly className: string;
}

const STATUS_META: Record<CanvasCompileStatus, StatusMeta> = {
  pending: { label: msg`In coda`, className: "bg-[#FEF3C7] text-[#B45309]" },
  compiling: { label: msg`Compilazione`, className: "bg-[#FEF3C7] text-[#B45309]" },
  ready: { label: msg`Pronto`, className: "bg-[#D1FAE5] text-[#047857]" },
  stale: {
    label: msg`Aggiornabile`,
    className: "border border-ando-orange/40 bg-transparent text-ando-orange",
  },
  failed: { label: msg`Errore`, className: "bg-[#FEE2E2] text-[#DC2626]" },
  archived: { label: msg`Archiviato`, className: "bg-[#E5E5E4] text-[#6B6864]" },
};

export const CanvasStatusChip: React.FC<{ status: CanvasCompileStatus }> = ({ status }) => {
  const { i18n } = useLingui();
  const meta = STATUS_META[status];
  return (
    <span
      className={`shrink-0 rounded-md px-2 py-0.5 font-medium text-[10px] tabular-nums ${meta.className}`}
    >
      {i18n._(meta.label)}
    </span>
  );
};
CanvasStatusChip.displayName = "CanvasStatusChip";
